import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useAuth } from "@clerk/clerk-react"
import type { DemoThreadPayload, OnboardingFlags } from "../../types"
import {
  afterClerkSignup,
  fetchOnboardingBootstrap,
  fetchOnboardingDemoThread,
  requestStripeConnectLink,
  saveOnboardingA2P,
  syncOnboardingVerification,
  type AfterClerkSignupResponse,
  type SyncVerificationOptions,
  type SyncVerificationResult,
} from "../../api/onboarding"
import { useDemoMode } from "../../demo/DemoProvider"
import { ApiError, resolveCorrelationId } from "../../api/http"
import { useTenantOnboarding } from "../../hooks/useTenantOnboarding"

type OnboardContextValue = {
  firstName: string
  lastName: string
  businessName: string
  email: string
  phone: string
  fakeNumber: string
  demonStatus: string
  preUserId: string | null
  emailVerified: boolean
  phoneVerified: boolean
  flags: OnboardingFlags
  loading: boolean
  channels: { sms: boolean; whatsapp: boolean }
  toggleChannel: (key: "sms" | "whatsapp") => void
  assignedNumber: string
  setAssignedNumber: (value: string) => void
  setFirstName: (value: string) => void
  setLastName: (value: string) => void
  setBusinessName: (value: string) => void
  setEmail: (value: string | ((prev: string) => string)) => void
  setPhone: (value: string) => void
  setEmailVerified: (value: boolean) => void
  setPhoneVerified: (value: boolean) => void
  submitContact: (input: { token: string; email?: string; phone: string; businessName?: string | null; firstName?: string | null; lastName?: string | null; country?: string }) => Promise<AfterClerkSignupResponse>
  fetchDemoThread: () => Promise<DemoThreadPayload>
  requestStripeLink: () => Promise<string>
  saveA2P: (fields: Record<string, unknown>) => Promise<void>
  refreshBootstrap: (params?: { preUserId?: string; forceVariant?: string }) => Promise<void>
  syncVerification: (token?: string, options?: SyncVerificationOptions) => Promise<{ provisioned: boolean; emailVerified: boolean; phoneVerified: boolean }>
  verificationStatus: VerificationSyncStatus
  refetchTenant: () => Promise<void>
}

type VerificationSyncStatus = {
  state: "idle" | "pending" | "success" | "error"
  strategy?: "session" | "lookup"
  timestamp?: number
  correlationId?: string
  result?: SyncVerificationResult
  error?: string
}

const DEFAULT_FAKE_NUMBER = "+1 (555) 210-8ᚠᛉ"
const LEGACY_DEFAULT_NUMBER = "+1 555 900 6666"
const DEFAULT_FLAGS: OnboardingFlags = {
  "onboarding.getDemonNumberV1": false,
  "onboarding.phoneOtp": false,
}

const STORAGE_KEYS = {
  preUserId: "onboard.preUserId",
  email: "onboard.email",
} as const

function readPersistedValue(key: string): string | null {
  if (typeof window === "undefined" || !window.localStorage) return null
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function persistValue(key: string, value: string | null | undefined): void {
  if (typeof window === "undefined" || !window.localStorage) return
  try {
    if (value && value.trim()) {
      window.localStorage.setItem(key, value.trim())
    } else {
      window.localStorage.removeItem(key)
    }
  } catch {
    /* ignore storage errors */
  }
}

function normalizeOptionalEmail(value?: string | null): string | undefined {
  if (typeof value !== "string") {
    return undefined
  }
  const trimmed = value.trim()
  return trimmed || undefined
}

const OnboardContext = createContext<OnboardContextValue | undefined>(undefined)

export function useOnboard() {
  const ctx = useContext(OnboardContext)
  if (!ctx) {
    throw new Error("useOnboard must be used within OnboardProvider")
  }
  return ctx
}

export function OnboardProvider({ children }: { children: ReactNode }) {
  const { demo } = useDemoMode()
  const { getToken } = useAuth()
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [businessName, setBusinessName] = useState("")
  const [emailState, setEmailState] = useState(() => readPersistedValue(STORAGE_KEYS.email) ?? "")
  const [phone, setPhone] = useState("")
  const [fakeNumber, setFakeNumber] = useState(DEFAULT_FAKE_NUMBER)
  const [demonStatus, setDemonStatus] = useState("PENDING_EMAIL")
  const [preUserIdState, setPreUserIdState] = useState<string | null>(() => readPersistedValue(STORAGE_KEYS.preUserId))
  const [flags, setFlags] = useState<OnboardingFlags>(DEFAULT_FLAGS)
  const [loading, setLoading] = useState<boolean>(true)
  const [channels, setChannels] = useState<{ sms: boolean; whatsapp: boolean }>({ sms: true, whatsapp: false })
  const [assignedNumber, setAssignedNumber] = useState(LEGACY_DEFAULT_NUMBER)
  const [emailVerified, setEmailVerifiedState] = useState(false)
  const [phoneVerified, setPhoneVerifiedState] = useState(false)
  const [verificationStatus, setVerificationStatus] = useState<VerificationSyncStatus>({ state: "idle" })
  const { tenant, refetch: refetchTenant } = useTenantOnboarding()

  // Sync tenant data from the hook into context state
  useEffect(() => {
    if (tenant) {
      if (tenant.twilio_number) {
        setAssignedNumber(tenant.twilio_number)
      }
      if (tenant.twilio_number_status) {
        setDemonStatus(tenant.twilio_number_status.toUpperCase())
      }
      // If we have a tenant, we can assume phone is verified (since it requires auth)
      // unless we want to rely strictly on the flag
      setPhoneVerifiedState(true)
    }
  }, [tenant])

  const setEmail = useCallback((value: string | ((prev: string) => string)) => {
    setEmailState((prev) => {
      const next = typeof value === "function" ? value(prev) : value
      persistValue(STORAGE_KEYS.email, next)
      return next
    })
  }, [])

  const setPreUserId = useCallback(
    (value: string | null) => {
      setPreUserIdState(value)
      persistValue(STORAGE_KEYS.preUserId, value)
    },
    [],
  )

  const refreshBootstrap = useCallback(
    async (params: { preUserId?: string; forceVariant?: string } = {}) => {
      setLoading(true)
      try {
        const inferredPreUserId =
          params.preUserId ?? preUserIdState ?? readPersistedValue(STORAGE_KEYS.preUserId) ?? undefined
        const effectiveParams = inferredPreUserId ? { ...params, preUserId: inferredPreUserId } : params
        const data = await fetchOnboardingBootstrap(demo, effectiveParams)
        setFlags(data.flags ?? DEFAULT_FLAGS)

        const preUser = data.preUser
        const demon = data.demon

        if (preUser) {
          setPreUserId(preUser.id)
          setFakeNumber(preUser.mask ?? DEFAULT_FAKE_NUMBER)
          setDemonStatus(preUser.status ?? demon?.status ?? "PENDING_EMAIL")
          setAssignedNumber((prev) => prev || LEGACY_DEFAULT_NUMBER)
        } else {
          setPreUserId(null)
        }

        if (demon) {
          setFakeNumber((prev) => demon.mask ?? demon.realNumber ?? prev)
          setDemonStatus((prev) => demon.status ?? prev)
          setAssignedNumber((prev) => demon.realNumber ?? prev)
        }

        const incomingEmail = preUser?.email ?? null
        if (incomingEmail) {
          setEmail((prev) => (prev && prev.toLowerCase() === incomingEmail.toLowerCase() ? prev : incomingEmail))
        }

        const incomingPhone = preUser?.phone ?? null
        if (typeof incomingPhone === "string" && incomingPhone.trim()) {
          setPhone((prev) => (prev === incomingPhone ? prev : incomingPhone))
        }

        const statusSource = preUser?.status ?? demon?.status ?? "PENDING_EMAIL"
        const normalizedStatus = typeof statusSource === "string" ? statusSource.toUpperCase() : ""
        const emailStatusSet = new Set(["EMAIL_CONFIRMED", "PHONE_CONFIRMED", "PHONE_VERIFIED", "PROVISIONING", "PROVISIONED", "READY", "ACTIVE", "COMPLETE"])
        const phoneStatusSet = new Set(["PHONE_CONFIRMED", "PHONE_VERIFIED", "PROVISIONING", "PROVISIONED", "READY", "ACTIVE", "COMPLETE"])

        const derivedEmailVerified = Boolean(data.user?.emailVerified) || emailStatusSet.has(normalizedStatus)
        const derivedPhoneVerified = Boolean(data.user?.phoneVerified) || phoneStatusSet.has(normalizedStatus)

        setEmailVerifiedState((prev) => prev || derivedEmailVerified)
        setPhoneVerifiedState((prev) => prev || derivedPhoneVerified)
      } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          setPreUserId(null)
        }
        throw error
      } finally {
        setLoading(false)
      }
    },
    [demo, preUserIdState, setEmail, setPreUserId],
  )

  useEffect(() => {
    refreshBootstrap().catch(() => setLoading(false))
  }, [refreshBootstrap])

  const submitContact = useCallback(
    async (input: { token: string; email?: string; phone: string; businessName?: string | null; firstName?: string | null; lastName?: string | null; country?: string }): Promise<AfterClerkSignupResponse> => {
      const normalizedPhone = input.phone.trim()
      const resolvedBusinessName = input.businessName ?? businessName ?? undefined
      const resolvedFirstName = input.firstName ?? firstName ?? undefined
      const resolvedLastName = input.lastName ?? lastName ?? undefined
      const normalizedInputEmail = normalizeOptionalEmail(input.email)
      const normalizedStoredEmail = normalizeOptionalEmail(emailState)
      const resolvedEmail = normalizedInputEmail ?? normalizedStoredEmail
      if (demo) {
        const mock: AfterClerkSignupResponse = {
          tenantId: 1,
          twilioNumber: LEGACY_DEFAULT_NUMBER,
          twilioStatus: "active",
        }
        setEmail(resolvedEmail ?? "")
        setPhone(normalizedPhone)
        setFakeNumber(mock.twilioNumber || DEFAULT_FAKE_NUMBER)
        setAssignedNumber(mock.twilioNumber || LEGACY_DEFAULT_NUMBER)
        setDemonStatus("ACTIVE")
        setPhoneVerifiedState(true)
        if (normalizedInputEmail) {
          setEmailVerifiedState(true)
        }
        setPreUserId(null)
        return mock
      }
      const response = await afterClerkSignup(
        input.token,
        {
          email: resolvedEmail,
          phone: normalizedPhone,
          businessName: resolvedBusinessName,
          firstName: resolvedFirstName,
          lastName: resolvedLastName,
          country: input.country,
        },
        demo,
      )
      setPreUserId(null)
      setEmail(resolvedEmail ?? "")
      setPhone(normalizedPhone)
      // Ensure we store the assigned number correctly
      if (response.twilioNumber) {
        setFakeNumber(response.twilioNumber)
        setAssignedNumber(response.twilioNumber)
      }
      setDemonStatus(response.twilioStatus?.toUpperCase() || "PROVISIONING")
      setPhoneVerifiedState(true)
      if (normalizedInputEmail) {
        setEmailVerifiedState(true)
      }
      return response
    },
    [businessName, demo, emailState, firstName, lastName, setAssignedNumber, setDemonStatus, setEmail, setEmailVerifiedState, setFakeNumber, setPhone, setPhoneVerifiedState, setPreUserId],
  )

  const fetchDemoThread = useCallback(async (): Promise<DemoThreadPayload> => {
    return fetchOnboardingDemoThread(demo)
  }, [demo])

  const requestStripeLink = useCallback(async (): Promise<string> => {
    if (demo) {
      return requestStripeConnectLink(true)
    }
    const token = await getToken()
    if (!token) {
      throw new Error("missing_auth_token")
    }
    return requestStripeConnectLink(false, token)
  }, [demo, getToken])

  const saveA2P = useCallback(
    async (fields: Record<string, unknown>) => {
      await saveOnboardingA2P(demo, fields)
    },
    [demo],
  )

  const syncVerification = useCallback(
    async (token?: string, options?: SyncVerificationOptions): Promise<{ provisioned: boolean; emailVerified: boolean; phoneVerified: boolean }> => {
      const storedPreUserId = preUserIdState ?? readPersistedValue(STORAGE_KEYS.preUserId)
      const storedEmail = emailState || readPersistedValue(STORAGE_KEYS.email) || undefined
      const correlationId = resolveCorrelationId()
      const enrichedOptions =
        options || storedPreUserId || storedEmail
          ? {
              ...options,
              preUserId: options?.preUserId ?? storedPreUserId ?? undefined,
              email: options?.email ?? storedEmail ?? undefined,
            }
          : undefined
      const strategyUsed = enrichedOptions?.strategy ?? (token ? "session" : "lookup")
      setVerificationStatus({ state: "pending", strategy: strategyUsed, timestamp: Date.now(), correlationId })
      try {
        const result = await syncOnboardingVerification(demo, token, enrichedOptions)
        if (result.emailVerified) {
          setEmailVerifiedState(true)
        }
        if (result.phoneVerified) {
          setPhoneVerifiedState(true)
        }
        setVerificationStatus({
          state: "success",
          strategy: strategyUsed,
          timestamp: Date.now(),
          correlationId,
          result,
        })
        await refreshBootstrap()
        return result
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        setVerificationStatus({ state: "error", strategy: strategyUsed, timestamp: Date.now(), correlationId, error: message })
        throw error
      }
    },
    [demo, emailState, preUserIdState, refreshBootstrap, setEmailVerifiedState, setPhoneVerifiedState],
  )

  const value = useMemo<OnboardContextValue>(
    () => ({
      firstName,
      lastName,
      businessName,
      email: emailState,
      phone,
      fakeNumber,
      demonStatus,
      preUserId: preUserIdState,
      emailVerified,
      phoneVerified,
      flags,
      loading,
      channels,
      toggleChannel: (key) =>
        setChannels((prev) => {
          const next = { ...prev, [key]: !prev[key] }
          if (!next.sms && !next.whatsapp) {
            next.sms = true
          }
          return next
        }),
      assignedNumber,
      setAssignedNumber,
      setFirstName,
      setLastName,
      setBusinessName,
      setEmail,
      setPhone,
      setEmailVerified: setEmailVerifiedState,
      setPhoneVerified: setPhoneVerifiedState,
      submitContact,
      fetchDemoThread,
      requestStripeLink,
      saveA2P,
      refreshBootstrap,
      syncVerification,
      verificationStatus,
      refetchTenant,
    }),
    [
      assignedNumber,
      businessName,
      channels,
      demonStatus,
      emailState,
      emailVerified,
      fakeNumber,
      fetchDemoThread,
      firstName,
      flags,
      lastName,
      loading,
      phone,
      phoneVerified,
      preUserIdState,
      refreshBootstrap,
      requestStripeLink,
      saveA2P,
      setAssignedNumber,
      setBusinessName,
      setEmail,
      setEmailVerifiedState,
      setFirstName,
      setLastName,
      setPhone,
      setPhoneVerifiedState,
      submitContact,
      syncVerification,
      verificationStatus,
      refetchTenant,
    ],
  )

  return <OnboardContext.Provider value={value}>{children}</OnboardContext.Provider>
}
